/**
 * 平台管理员 API
 */
import request from './api';

export interface Agent {
  id: string;
  username: string;
  nickname: string;
  phone: string;
  email: string;
  site_count: number;
  plan: string;
  status: 'active' | 'suspended' | 'trial';
  expire_at?: string;
  created_at: string;
}

export interface DashboardStats {
  agent_count: number;
  site_count: number;
  user_count: number;
  listing_count: number;
}

export interface TodayStats {
  agents: number;
  sites: number;
  users: number;
  listings: number;
}

export interface RecentAgent {
  id: string;
  username: string;
  nickname: string;
  site_count: number;
  status: string;
  created_at: string;
}

export interface PlanStat {
  plan_id: string;
  plan_name: string;
  count: number;
}

export interface SiteStat {
  active: number;
  stopped: number;
  expired: number;
}

export interface DashboardData {
  stats: DashboardStats;
  today: TodayStats;
  recent_agents: RecentAgent[];
  plan_stats: PlanStat[];
  site_stats: SiteStat;
}

/**
 * 获取平台统计概览
 */
export function getPlatformDashboard(): Promise<DashboardData> {
  return request<DashboardData>('/platform/dashboard');
}

/**
 * 获取代理商列表
 */
export function getAgents(params?: {
  page?: number;
  limit?: number;
  status?: string;
  search?: string;
}): Promise<{ list: Agent[]; total: number; page: number; limit: number }> {
  return request('/platform/agent', { params });
}

/**
 * 创建代理商
 */
export function createAgent(data: {
  username: string;
  password: string;
  nickname: string;
  phone?: string;
  email?: string;
  plan_id?: string;
  plan_expire_at?: string;
}): Promise<{ id: string; username: string; nickname: string }> {
  return request('/platform/agent', { method: 'POST', body: JSON.stringify(data) });
}

/**
 * 更新代理商
 */
export function updateAgent(id: string, data: Partial<{
  nickname: string;
  phone: string;
  email: string;
  status: string;
  plan_id: string;
  plan_expire_at: string;
  password: string;
}>): Promise<void> {
  return request(`/platform/agent/${id}`, { method: 'PUT', body: JSON.stringify(data) });
}

/**
 * 删除代理商
 */
export function deleteAgent(id: string): Promise<void> {
  return request(`/platform/agent/${id}`, { method: 'DELETE' });
}

/**
 * 获取代理商详情
 */
export function getAgentDetail(id: string): Promise<Agent & { sites: any[]; plans: any[] }> {
  return request(`/platform/agent/${id}`);
}

// ==================== 套餐管理 ====================

export interface Plan {
  id: string;
  name: string;
  amount: number;
  status: 'active' | 'inactive';
  site_limit: number;
  agent_count: number;
  benefits: {
    site_limit?: number;
    features?: string[];
  };
  created_at: string;
}

/**
 * 获取套餐列表
 */
export function getPlans(): Promise<{ list: Plan[]; stats: { total: number; active: number } }> {
  return request('/platform/plans');
}

/**
 * 创建套餐
 */
export function createPlan(data: {
  name: string;
  amount: number;
  site_limit?: number;
  features?: string[];
}): Promise<{ id: string; name: string; amount: number }> {
  return request('/platform/plans', { method: 'POST', body: JSON.stringify(data) });
}

/**
 * 更新套餐
 */
export function updatePlan(id: string, data: Partial<{
  name: string;
  amount: number;
  status: string;
  site_limit: number;
  features: string[];
}>): Promise<void> {
  return request(`/platform/plans/${id}`, { method: 'PUT', body: JSON.stringify(data) });
}

/**
 * 删除套餐
 */
export function deletePlan(id: string): Promise<void> {
  return request(`/platform/plans/${id}`, { method: 'DELETE' });
}

/**
 * 获取套餐详情
 */
export function getPlanDetail(id: string): Promise<Plan> {
  return request(`/platform/plans/${id}`);
}

// ==================== 站点管理 ====================

export interface Site {
  id: string;
  name: string;
  subdomain: string;
  tenant_name: string;
  status: string;
  created_at: string;
  listing_count: number;
  user_count: number;
}

/**
 * 获取平台站点列表
 */
export function getPlatformSites(params?: {
  page?: number;
  limit?: number;
  status?: string;
  search?: string;
}): Promise<{ list: Site[]; total: number }> {
  return request('/platform/sites', { params });
}
